import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, CheckCircle2 } from "lucide-react";

const KEY = "mt_newsletter_seen_v1";
const API = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(true);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  useEffect(() => {
    if (localStorage.getItem(KEY)) return undefined;
    const t = setTimeout(() => setOpen(true), 5000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!consent) {
      setError("Please agree to receive emails to continue.");
      return;
    }
    setStatus("loading");
    try {
      const res = await fetch(`${API}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, consent, source: "homepage_popup" }),
      });
      if (!res.ok) throw new Error("bad");
      setStatus("success");
      localStorage.setItem(KEY, "1");
    } catch (err) {
      setStatus("error");
      setError("Something went wrong. Please try again.");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Newsletter signup"
      data-testid="newsletter-popup"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          data-testid="newsletter-close"
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/20 text-white transition hover:bg-black/40"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>

        {/* Header */}
        <div className="bg-neutral-900 px-6 py-6 text-center">
          <span className="text-xl font-extrabold uppercase tracking-wide text-white sm:text-2xl">
            Mattress<span className="text-[#D0021B]">Town</span>
          </span>
        </div>

        {status === "success" ? (
          <div className="px-6 py-10 text-center sm:px-10">
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#D0021B]" aria-hidden="true" />
            <h2 className="mt-4 text-2xl font-bold uppercase text-neutral-900">You're In!</h2>
            <p className="mt-2 text-neutral-600">
              Check your inbox for exclusive MattressTown deals. Happy sleeping!
            </p>
            <button
              onClick={close}
              data-testid="newsletter-success-close"
              className="mt-6 rounded-full bg-[#D0021B] px-8 py-3 text-sm font-bold uppercase tracking-wide text-white hover:bg-[#a80016]"
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="px-6 py-7 text-center sm:px-9">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#D0021B]">Fall Sale!</p>
            <h2 className="mt-2 text-2xl font-extrabold uppercase leading-tight text-neutral-900 sm:text-3xl">
              Save up to $1,000
            </h2>
            <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-neutral-500">
              On Top Brand Mattresses
            </p>
            <p className="mx-auto mt-3 max-w-xs text-sm text-neutral-600">
              Sign up for our newsletter and unlock exclusive deals, new arrivals and sleep tips.
            </p>

            <form onSubmit={submit} className="mt-5 space-y-3 text-left">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                data-testid="newsletter-email"
                className="w-full rounded-lg border border-neutral-300 px-4 py-3 text-base focus:border-[#D0021B] focus:outline-none focus:ring-1 focus:ring-[#D0021B]"
              />
              <label className="flex items-start gap-2 text-xs leading-relaxed text-neutral-500">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="newsletter-consent"
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-[#D0021B] focus:ring-[#D0021B]"
                />
                <span>
                  I agree to receive marketing emails from MattressTown and accept the{" "}
                  <Link to="/accessibility-statement" className="underline hover:text-[#D0021B]" onClick={close}>
                    Terms &amp; Privacy Policy
                  </Link>
                  . Consent is not a condition of purchase; unsubscribe anytime.
                </span>
              </label>
              {error && (
                <p className="text-xs font-semibold text-[#D0021B]" data-testid="newsletter-error">{error}</p>
              )}
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="newsletter-submit"
                className="w-full rounded-full bg-[#D0021B] px-8 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#a80016] disabled:opacity-60"
              >
                {status === "loading" ? "Signing you up..." : "Unlock My Offer"}
              </button>
            </form>
            <button
              type="button"
              onClick={close}
              data-testid="newsletter-decline"
              className="mt-3 text-xs font-medium text-neutral-400 underline hover:text-neutral-600"
            >
              No thanks, I'll pay full price
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
